import { useState } from "react";
import Icon from "./Icon";
import { GlassCard, Btn, PillTag } from "./UI";
import AwardBadge from "./AwardBadge";
import { useStoreMutation, mutations, queries, type Recipe } from "@/lib/store";

/**
 * ShareRecipeSheet
 *
 * Two-mode share sheet, scoped inside MNF only:
 *
 *   pair       Quietly hand the recipe to a sister pair partner.
 *   community  Make the recipe public so it shows up in Trending.
 *
 * Also previews the "Made in MNF" card that future viral-share PNG export
 * will render. For now the preview is in-app only — no external download.
 *
 * Sharing rules:
 *   - Sister-authored recipes can be made public; they then enter pendingReview
 *     so a future moderation queue can vet them.
 *   - Imported recipes can also be made public, same pendingReview flag.
 *   - Kiley-authored recipes are always public; we hide the community toggle
 *     and just show the share-with-pair option.
 */

type Mode = "pair" | "community";

export default function ShareRecipeSheet({
  recipe,
  open,
  onClose,
}: {
  recipe: Recipe;
  open: boolean;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<Mode>("pair");
  const [paired, setPaired] = useState<boolean>(false);
  const setRecipePublic = useStoreMutation(mutations.setRecipePublic);
  const author = queries.recipeAuthor(recipe.authorId);

  if (!open) return null;

  // Kiley's recipes stay public, no toggle. Sister and imported recipes can
  // be made public, which sets pendingReview for the future moderation queue.
  const canTogglePublic = recipe.source !== "kiley";

  const onSharePair = () => {
    // Pair share is in-app only; we just confirm visually for now.
    setPaired(true);
    setTimeout(() => {
      setPaired(false);
      onClose();
    }, 1400);
  };

  const onMakePublic = () => {
    setRecipePublic(recipe.id, true);
    onClose();
  };
  const onMakePrivate = () => {
    setRecipePublic(recipe.id, false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 60 }}
      onClick={onClose}
      data-testid="share-recipe-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Share Your Journey</p>
            <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}>
              Pass this recipe along
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-share"
            className="hover-elevate"
            style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Made in MNF preview card */}
        <div
          className="mnf-fade-up mb-5"
          style={{
            borderRadius: 20,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%), " +
              recipe.heroGradient,
            padding: 18,
            color: "#F5F0E8",
            position: "relative",
            overflow: "hidden",
            minHeight: 180,
            border: "1px solid rgba(30,74,53,0.10)",
            boxShadow: "0 14px 28px rgba(30,74,53,0.18)",
          }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="mnf-eyebrow" style={{ color: "rgba(245,240,232,0.75)" }}>Made in MNF</div>
            {recipe.awardTier !== "none" && (
              <AwardBadge tier={recipe.awardTier} count={recipe.lovedCount} size="sm" />
            )}
          </div>
          <h3 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.15, color: "#fff" }}>
            {recipe.title}
          </h3>
          <p className="mnf-body mt-2" style={{ fontSize: 11.5, color: "rgba(245,240,232,0.85)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
            {recipe.kcal} kcal · {recipe.proteinG}g protein · {recipe.fiberG}g fiber
          </p>
          <div className="flex items-center gap-2 mt-3">
            <PillTag tone="forest">
              <Icon name="schedule" size={11} color="#F5F0E8" /> {recipe.durationMin} min
            </PillTag>
            <PillTag tone="coral">By {author.isCoach ? "Coach Kiley" : author.name}</PillTag>
          </div>
        </div>

        {/* Mode tabs */}
        <div
          className="flex gap-1 mb-4 mnf-body"
          style={{ background: "rgba(30,74,53,0.06)", borderRadius: 12, padding: 4 }}
          role="tablist"
        >
          <button
            data-testid="tab-share-pair"
            role="tab"
            aria-selected={mode === "pair"}
            onClick={() => setMode("pair")}
            className="flex-1 hover-elevate"
            style={{
              padding: "9px 12px",
              borderRadius: 9,
              background: mode === "pair" ? "var(--mnf-cream)" : "transparent",
              color: mode === "pair" ? "var(--mnf-fd)" : "var(--mnf-tg)",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              border: "1px solid transparent",
              boxShadow: mode === "pair" ? "0 1px 3px rgba(30,74,53,0.1)" : "none",
              cursor: "pointer",
            }}
          >
            With your pair
          </button>
          <button
            data-testid="tab-share-community"
            role="tab"
            aria-selected={mode === "community"}
            onClick={() => setMode("community")}
            className="flex-1 hover-elevate"
            style={{
              padding: "9px 12px",
              borderRadius: 9,
              background: mode === "community" ? "var(--mnf-cream)" : "transparent",
              color: mode === "community" ? "var(--mnf-fd)" : "var(--mnf-tg)",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              border: "1px solid transparent",
              boxShadow: mode === "community" ? "0 1px 3px rgba(30,74,53,0.1)" : "none",
              cursor: "pointer",
            }}
          >
            With the garden
          </button>
        </div>

        {mode === "pair" ? (
          <GlassCard padding={16}>
            <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Send to your sister pair
            </p>
            <p className="mnf-body mt-1.5" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
              She'll see this in your shared check-in. No likes, no comments. Just two sisters cooking together.
            </p>
            <div className="mt-4">
              {paired ? (
                <Btn variant="ghost" block size="lg" disabled>
                  <Icon name="check_circle" size={18} filled /> Sent to your pair
                </Btn>
              ) : (
                <Btn data-testid="button-share-pair-confirm" variant="primary" block size="lg" onClick={onSharePair}>
                  <Icon name="ios_share" size={18} /> Share with my pair
                </Btn>
              )}
            </div>
          </GlassCard>
        ) : (
          <GlassCard padding={16}>
            <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Share with the garden
            </p>
            <p className="mnf-body mt-1.5" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
              {canTogglePublic
                ? "Your recipe joins Trending where every sister can find it. We'll review before it's featured, to keep the garden tended."
                : "This recipe is curated by Coach Kiley and is already part of the garden. Sharing with your pair is the warm way to pass it along."}
            </p>
            {canTogglePublic && (
              <div className="mt-4 grid grid-cols-1 gap-2.5">
                {recipe.public ? (
                  <Btn data-testid="button-make-private" variant="secondary" block size="lg" onClick={onMakePrivate}>
                    <Icon name="lock" size={18} /> Make private again
                  </Btn>
                ) : (
                  <Btn data-testid="button-make-public" variant="primary" block size="lg" onClick={onMakePublic}>
                    <Icon name="public" size={18} /> Share with the garden
                  </Btn>
                )}
                {recipe.pendingReview && (
                  <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-gold)", textAlign: "center", lineHeight: 1.5 }}>
                    Pending coach review. Your sisters will see it once Kiley signs off.
                  </p>
                )}
              </div>
            )}
            {!canTogglePublic && (
              <div className="mt-4">
                <Btn variant="ghost" block size="lg" disabled>
                  <Icon name="check_circle" size={18} filled /> Already in the garden
                </Btn>
              </div>
            )}
          </GlassCard>
        )}

        <p className="mnf-body mt-4" style={{ fontSize: 11, color: "var(--mnf-tg)", textAlign: "center", lineHeight: 1.5 }}>
          MNF keeps recipes inside our walls. No external posts, no algorithm chasing.
        </p>
      </div>
    </div>
  );
}
